import {TanStackTable} from "../../Components/Tables/TanStackTable";
import {BaseCard} from "../../Components/Cards/BaseCard";
import {createColumnHelper} from "@tanstack/react-table";
import {truncateString} from "../../Utils/StringHelper";
import {PrimaryButton} from "../../Components/Buttons/PrimaryButton";
import {TextInput} from "../../Components/Form/TextInput";
import {SecondaryButton} from "../../Components/Buttons/SecondaryButton";
import {useBoundStore} from "../../Store/store";
import {useEffect, useState} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faDownload} from "@fortawesome/free-solid-svg-icons";
import {useNavigate} from "react-router-dom";
import { useTranslation } from "react-i18next";
import {Link} from "react-router-dom";
import {faArrowRight} from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";

export const MyContracts = () => {
    const contracts = useBoundStore((state) => state.contracts);
    const contractsMetaData = useBoundStore((state) => state.contractsMetaData);
    const getMyContracts = useBoundStore((state) => state.getAllMyContracts);
    const [statusFilter, setStatusFilter] = useState([]);
    const [forceNewData, setForceNewData] = useState(false);
    const {t} = useTranslation();

    const [currentPage, setCurrentPage] = useState(1);
    const [searchFilter, setSearchFilter] = useState('');

    const handleFilterClick = (status) => {
        if(statusFilter.includes(status)) {
            setStatusFilter(statusFilter.filter(s => s !== status))
        } else {
            setStatusFilter([...statusFilter, status])
        }
    }

    useEffect(() => {
        setForceNewData(!forceNewData);
    }, [contracts]);

    useEffect(() => {
        setCurrentPage(1);
    }, [statusFilter, searchFilter]);

    useEffect(() => {
        getMyContracts(statusFilter, currentPage, searchFilter);
    }, [getMyContracts, statusFilter, currentPage, searchFilter]);

    const columnHelper = createColumnHelper()
    const columns = [
        columnHelper.accessor('branch', {
            header: t('general.fields.customer'),
            cell: props => truncateString(`${props.cell.getValue()?.company_name ?? '-'}`),
        }),
        columnHelper.accessor('freelancer', {
            header: t('general.fields.freelancer'),
            cell: props => truncateString(`${props.cell.getValue()?.first_name ?? '-'} ${props.cell.getValue()?.last_name}`),
        }),
        columnHelper.accessor('id', {
            header: t('general.fields.contract_number'),
            cell: props => truncateString(props.cell.getValue()),
        }),
        columnHelper.accessor('created_at', {
            header: t('general.fields.created_at'),
            cell: props => {
                return dayjs.unix(props.cell.getValue().timestamp).format('DD/MM/YYYY HH:mm')
            },
        }),
        columnHelper.accessor('document', {
            header: t('general.fields.document'),
            cell: props => {
                if((props.cell.getValue() !== null && props.cell.getValue() !== undefined) && props.cell.getValue().hasOwnProperty('url') ) {
                    return (
                        <div className={'flex flex-row space-x-4'}>
                            <FontAwesomeIcon icon={faDownload} className={'text-primary cursor-pointer'} onClick={() => window.open(props.cell.getValue().url, '_blank')}/>
                        </div>
                    )
                } else {
                    return (<p>-</p>)
                }
            },
        }),
        columnHelper.accessor('actions', {
            header: t('general.fields.status'),
            cell: props => {
                return (
                    <div className={'flex flex-row space-x-4'}>
                        <div>
                            {
                                ['pending'].includes(props.row.original.status) && (
                                    <div className={'px-2 py-1 bg-orange-300 text-orange-900 rounded-full text-xs opacity-70'}>{t('contracts.status_to_sign')}</div>
                                )
                            }

                            {
                                ['sent'].includes(props.row.original.status) && (
                                    <div className={'px-2 py-1 bg-yellow-300 text-yellow-900 rounded-full text-xs opacity-70'}>{t('contracts.status_sent')}</div>
                                )
                            }

                            {
                                ['completed'].includes(props.row.original.status) && (
                                    <div className={'px-2 py-1 bg-green-300 text-green-900 rounded-full text-xs opacity-70'}>{t('contracts.status_signed')}</div>
                                )
                            }

                            {
                                ['cancelled'].includes(props.row.original.status) && (
                                    <div className={'px-2 py-1 bg-red-300 text-red-900 rounded-full text-xs opacity-70'}>{t('contracts.status_cancelled')}</div>
                                )
                            }

                        </div>
                    </div>
                )
            },
        }),
        columnHelper.accessor('details', {
            header: '',
            cell: props => {
                return (
                    <div className={'flex flex-row space-x-4'}>
                        <Link
                            to={`/customer/contracts/${props.row.original.id}`}
                            className={'flex bg-primary w-7 h-7 rounded-full text-white flex-col justify-center border border-primary hover:bg-transparent hover:border-primary hover:text-primary transition-all duration-300'}
                        >
                            <FontAwesomeIcon icon={faArrowRight} className={'text-sm'}/>
                        </Link>
                    </div>
                )
            },
        })
    ]

    return (
        <div>
            <div className={'mb-2 '}>
                <div className={'flex justify-between'}>
                    <div className={'my-auto'}>
                        <TextInput
                            type={'text'}
                            name={'search'}
                            translationIndex={null}
                            placeholder={t('general.actions.search')}
                            onChange={(e) => e.target.value.length > 2 || e.target.value.length === 0  ? setSearchFilter(e.target.value) : null}
                        >
                        </TextInput>
                    </div>
                    <div className={'grid grid-cols-3 gap-4 my-auto'}>
                        <div>
                            <SecondaryButton outlined={!statusFilter.includes('pending')} onClick={() => {
                                handleFilterClick('pending')
                            }}>
                                {t('general.actions.to_sign')}
                            </SecondaryButton>
                        </div>
                        <div>
                            <SecondaryButton outlined={!statusFilter.includes('sent')} onClick={() => {
                                handleFilterClick('sent')
                            }}>
                                {t('contracts.status_sent')}
                            </SecondaryButton>
                        </div>
                        <div>
                            <SecondaryButton outlined={!statusFilter.includes('completed')} onClick={() => {
                                handleFilterClick('completed')
                            }}>
                                {t('general.actions.active_contracts')}
                            </SecondaryButton>
                        </div>
                    </div>
                </div>
            </div>
            <BaseCard>
                {contracts?.length === 0 ? (
                    <p data-testid="empty-state" className="text-sm text-gray-500 text-center py-8">{t('contracts.no_results')}</p>
                ) : (
                    <TanStackTable
                        columns={columns}
                        tableData={contracts}
                        forceNewData={forceNewData}
                        laravelPaginationMetaData={contractsMetaData}
                        onChangePage={(page) => {
                            setCurrentPage(page +1)
                        }}
                    />
                )}
            </BaseCard>
        </div>
    )
}